import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import LabelledInput from 'components/LabelledInput';
import { FormattedMessage, FormattedHTMLMessage, injectIntl } from 'react-intl';
import Button from 'widgets/Button';
import Divider from 'widgets/Divider';
import Loader from 'components/Loader';
import { applyRelativeToWindowPosition } from 'libs/windowDimension';
import { sectionList, processSubscriptionActivateUrlOnLogout } from 'libs/subscriptionActivate';
import { initRecurring } from 'libs/activate';
import { clickSignOutApp, changeSubscriptionActivateFields } from 'libs/cleverTap';
import AppleAuthButton from 'components/AppleAuthButton';
import GoogleAuthButton from 'components/GoogleAuthButton';
import Modal from 'components/Modal';
import {
  loginAccountDetails, signupAccountDetails, loginAccountDetailsRecurring,
  continueWithSocialAccountDetails, resetEmailAccountDetails, saveAddtionalInfoAccountDetails
} from 'redux/actions/subscriptionActivate';
import { handleFetchUser, handleLogoutUser } from 'redux/actions/auth';
import { ResetEmail, RetryLogin, Verification } from 'components/ModalContent';
import {
  getGoogleAuthCbParam, processAuthInfoForAdditionalInfo,
  authCallback, getValuesFromUrl
} from 'libs/auth';
import AdditionalInfoModal from './AdditionalInfoModal';
import defaultStyle from './style';
import SectionHeader from '../SectionHeader';
import {
  USER_INFO_KEYS, ERROR_INFO_KEYS, AUTH_FORM_ADDITIONAL_INFO_ERROR_CONFIG,
  AUTH_FORM_LOGIN_ERROR_CONFIG, RESET_EMAIL_ERROR_CONFIG, AUTH_FORM_SIGNUP_ERROR_CONFIG,
  checkErrors
} from './config';
import { AUTH0_CONNECTIONS } from 'components/Auth/config'
import './AccountDetails.scss';

function getMissedAdditionalInfo(userInfo, additionalInfo) {
  const missedAddtionalInfo = {};
  if (!additionalInfo[USER_INFO_KEYS.FIRST_NAME]) {
    missedAddtionalInfo[USER_INFO_KEYS.FIRST_NAME] = userInfo[USER_INFO_KEYS.FIRST_NAME];
  }

  if (!additionalInfo[USER_INFO_KEYS.LAST_NAME]) {
    missedAddtionalInfo[USER_INFO_KEYS.LAST_NAME] = userInfo[USER_INFO_KEYS.LAST_NAME];
  }

  if (!additionalInfo[USER_INFO_KEYS.DOB]) {
    missedAddtionalInfo[USER_INFO_KEYS.DOB] = userInfo[USER_INFO_KEYS.DOB];
  }

  missedAddtionalInfo[USER_INFO_KEYS.ACCESS_TOKEN] = userInfo[USER_INFO_KEYS.ACCESS_TOKEN];

  return missedAddtionalInfo;
}

class AccountDetails extends React.PureComponent {
  constructor(props) {
    super(props);
    const urlValues = getValuesFromUrl(['email', 'sign_up']);

    const signupParameter = () => {
      if (urlValues.sign_up === "false") {
        return false;
      } else {
        return true;
      }
    };

    this.state = {
      isSignup: signupParameter(),
      isAdditionalInfoMissed: false,
      forgotPasswordModalOpen: false,
      isRecurrLoginRetry: false,
      emailVerifyModalOpen: false,
      additionalInfoModalOpen: false,
      additionalInfo: {},
      userInfo: {
        [USER_INFO_KEYS.FIRST_NAME]: '',
        [USER_INFO_KEYS.LAST_NAME]: '',
        [USER_INFO_KEYS.DOB]: '',
        [USER_INFO_KEYS.EMAIL]: urlValues.email,
        [USER_INFO_KEYS.PASSWORD]: '',
        [USER_INFO_KEYS.RESET_EMAIL]: '',
        [USER_INFO_KEYS.ACCESS_TOKEN]: ''
      },
      errorInfo: {
        [ERROR_INFO_KEYS.AUTH_FORM]: {}, // for login/signup form
        [ERROR_INFO_KEYS.RESET_EMAIL_FORM]: {}, // for reset email form
        errorConfigAuth: AUTH_FORM_SIGNUP_ERROR_CONFIG,
      }
    };
    this.isAuthFormDirty = false;
    this.isResetEmailFormDirty = false;
    this.authInfo = {};
  }

  componentDidMount() {
    const { info } = this.props.auth;
    if (info) {
      this.props.checkoutSuccess();
    } else {
      const params = getGoogleAuthCbParam();
      if (params) {
        const { id_token, access_token, error_description } = params;
        if (error_description) {
          this.props.showErrorBar({ message: error_description });
        } else {
          const authInfo = {
            idToken: id_token,
            accessToken: access_token
          };
          const additionalInfo = processAuthInfoForAdditionalInfo(authInfo);
          this.handleAddtionalInfoCb(additionalInfo);
        }
      }
    }
  }

  setRecurrLoginRetry = () => {
    this.setState({ isRecurrLoginRetry: true });
  }

  resetRecurrLoginRetry = () => {
    this.setState({ isRecurrLoginRetry: false });
  }

  handleAuthSuccess = ({ accessToken }) => {
    const { app } = this.props;
    authCallback({ accessToken }, { restrictRedirect: true });
    this.props.handleFetchUser({ app });
  }

  handleChange = (e, cb) => {
    const userInfo = { ...this.state.userInfo };
    userInfo[e.target.name] = e.target.value;
    this.setState({ userInfo }, cb);
    changeSubscriptionActivateFields(e.target.name); // track event
  }

  handleAuthFormInputChange = e => this.handleChange(e, () => {
    if (this.isAuthFormDirty) {
      const { errorInfo: { errorConfigAuth } } = this.state;
      this.validateFields(errorConfigAuth);
    }
  });

  handleResetFormInputChange = e => this.handleChange(e, () => {
    if (this.isResetEmailFormDirty) {
      this.validateFields(RESET_EMAIL_ERROR_CONFIG);
    }
  })

  handleDobChange = (e) => {
    const { userInfo } = this.state;
    const dob = userInfo[USER_INFO_KEYS.DOB];
    let { value = '' } = e.target;

    if ((value.length === 2 || value.length === 5) && (dob.length < value.length)) {
      value = `${value}/`;
    }
    e.target.value = value;
    this.handleAuthFormInputChange(e);
  }

  handleAuthFormSave = () => {
    const { errorInfo: { errorConfigAuth } } = this.state;
    const errorInfo = this.validateFields(errorConfigAuth);

    this.isAuthFormDirty = true;

    if (Object.keys(errorInfo[ERROR_INFO_KEYS.AUTH_FORM]).length) {
      return;
    }

    const {
      isSignup, userInfo, isAdditionalInfoMissed, additionalInfo
    } = this.state;

    if (isAdditionalInfoMissed) {
      const _userInfo = getMissedAdditionalInfo(userInfo, additionalInfo);
      const restrictRedirect = true;
      this.props.saveAddtionalInfoAccountDetails(
        _userInfo, restrictRedirect, this.handleSaveCallback
      );
    } else if (isSignup) {
      this.props.signupAccountDetails(userInfo, this.handleSaveCallback);
    } else {
      this.props.loginAccountDetails(userInfo, this.handleSaveCallback);
    }
  }

  handleResetEmailPasswordSave = () => {
    const errorInfo = this.validateFields(RESET_EMAIL_ERROR_CONFIG);
    this.isResetEmailFormDirty = true;

    if (Object.keys(errorInfo[ERROR_INFO_KEYS.RESET_EMAIL_FORM]).length) {
      return;
    }

    const email = this.state.userInfo[USER_INFO_KEYS.RESET_EMAIL];

    this.props.resetEmailAccountDetails({ email }, this.handleSaveCallback);
  }

  handleAddtionalInfoCb = (additionalInfo = {}) => {
    const { fields = {} } = additionalInfo;
    if (additionalInfo.isAdditionalInfoMissed && fields.isSignup) {
      const { userInfo, errorInfo } = this.state;
      errorInfo.errorConfigAuth = AUTH_FORM_ADDITIONAL_INFO_ERROR_CONFIG;

      this.setState({
        isAdditionalInfoMissed: true,
        additionalInfoModalOpen: true,
        userInfo: {
          ...userInfo,
          ...fields,
          [USER_INFO_KEYS.ACCESS_TOKEN]: additionalInfo.accessToken
        },
        isSignup: false,
        additionalInfo: fields,
        errorInfo
      });
    } else {
      applyRelativeToWindowPosition();
      this.handleAuthSuccess({ accessToken: additionalInfo.accessToken });
    }
  }

  handleSocialAuthorisationContinue = (connection) => {
    const { userInfo } = this.state;
    userInfo.activationCode = this.props.activationCode;
    this.props.continueWithSocialAccountDetails(connection, userInfo);
  }

  handleSaveCallback = ({
    message, closeResetModal, recurringLogin, additionalInfo, closeEmailVerifyModal
  }) => {
    const { userInfo } = this.state;

    if (recurringLogin) {
      initRecurring({
        initCb: this.recurringInitCb,
        endCb: this.recurringEndCb,
        api: this.props.loginAccountDetailsRecurring,
        apiParams: [userInfo],
        successCb: this.handleSaveCallback,
      });
    }

    if (message) {
      this.props.showErrorBar({ message });
    }

    if (closeResetModal) {
      this.closeResetPasswordModal();
    }

    if (closeEmailVerifyModal) {
      this.closeEmailVerifyModal();
    }

    if (additionalInfo) {
      this.handleAddtionalInfoCb(additionalInfo);
    }
  }

  handleRetryRecurrLogin = () => {
    const { userInfo } = this.state;
    this.resetRecurrLoginRetry();
    initRecurring({
      initCb: this.recurringInitCb,
      endCb: this.recurringEndCb,
      api: this.props.loginAccountDetailsRecurring,
      apiParams: [userInfo],
      successCb: this.handleSaveCallback,
      isActivateStraightAway: true,
    });
  }

  recurringInitCb = () => {
    this.showEmailVerifyModal();
  }

  recurringEndCb = () => {
    this.setRecurrLoginRetry();
  }

  showResetPasswordModal = () => {
    this.setState({ forgotPasswordModalOpen: true });
  }

  closeResetPasswordModal = () => {
    this.setState({ forgotPasswordModalOpen: false });
  }

  showEmailVerifyModal = () => {
    this.setState({ emailVerifyModalOpen: true });
  }

  closeEmailVerifyModal = () => {
    this.setState({ emailVerifyModalOpen: false });
  }

  closeAdditionalInfoModal = () => {
    this.setState({ additionalInfoModalOpen: false });
  }

  validateFields = (config = {}) => {
    this.props.closeErrorBar();
    const { isSignup } = this.state;
    const userInfo = { ...this.state.userInfo };
    const errorInfo = { ...this.state.errorInfo };
    const { errorFields } = config;

    const errors = checkErrors(errorFields, userInfo, isSignup);
    errorInfo[config.errorKey] = errors;
    this.setState({ errorInfo });
    return errorInfo;
  }

  toggleView = () => {
    const { isSignup, errorInfo } = this.state;
    errorInfo.errorConfigAuth = !isSignup ?
      AUTH_FORM_SIGNUP_ERROR_CONFIG :
      AUTH_FORM_LOGIN_ERROR_CONFIG;
    errorInfo[ERROR_INFO_KEYS.AUTH_FORM] = {};

    this.setState({ isSignup: !isSignup, errorInfo });
  }

  handleLogoutUser = () => {
    this.props.handleLogoutUser({ restrictRedirect: true });
    this.props.setCurrentSection(sectionList.accountDetails);
    processSubscriptionActivateUrlOnLogout();
    clickSignOutApp(); // track event
  }

  render() {
    const {
      isSignup, emailVerifyModalOpen,
      userInfo, errorInfo, forgotPasswordModalOpen,
      isRecurrLoginRetry, additionalInfoModalOpen,
      additionalInfo
    } = this.state;

    const {
      currentSection, isActive,
      sectionHeader: { step, label, actionLabel },
      auth, subscriptionActivate: { accountDetails }, windowDimension,
      intl
    } = this.props;

    const isEdit = !!auth.info;
    const errorInfoAuthForm = errorInfo[ERROR_INFO_KEYS.AUTH_FORM] || {};
    const resetEmailErr = errorInfo[ERROR_INFO_KEYS.RESET_EMAIL_FORM][USER_INFO_KEYS.RESET_EMAIL];
    let EmailVerifyTitle = <FormattedMessage id="views.subscriptionActivate.verification.title" />;
    let EmailVerifyContent = Verification;
    if (isRecurrLoginRetry) {
      EmailVerifyTitle = (
        <FormattedMessage id="views.subscriptionActivate.retryLogin.title" />
      );

      EmailVerifyContent = () => (
        <RetryLogin
          onClick={this.handleRetryRecurrLogin}
          rootStyleButton={defaultStyle.AccountDetailsButtonStyle}
          labelStyleButton={defaultStyle.AccountDetailsButtonLabelStyle}
        />
      );
    }
    return (
      <div className="SubscriptionActivate__AccountDetails">
        <div className="SubscriptionActivate__AccountDetails__Content__Divider" />
        <SectionHeader
          step={step}
          label={label}
          isEdit={isEdit}
          handleEdit={this.handleLogoutUser}
          activeSection={currentSection}
          actionLabel={actionLabel}
          windowDimension={windowDimension}
        />
        {
          accountDetails.isProcessingAuth ?
            <div className="SubscriptionActivate__AccountDetails__Loader"><Loader /></div> :
            isActive &&
            <div>
              <Modal
                title={EmailVerifyTitle}
                showCross={false}
                open={emailVerifyModalOpen}
                {...defaultStyle.modalStyleVerifyEmail}
              >
                <div className="SubscriptionActivate__AccountDetails__Modal__EmailVerify__Content">
                  <EmailVerifyContent />
                </div>
              </Modal>
              <Modal
                title={intl.formatMessage({ id: 'views.subscriptionActivate.modal.resetEmail.title' })}
                open={forgotPasswordModalOpen}
                toggleModal={this.closeResetPasswordModal}
                {...defaultStyle.modalStyleResetPasswordEmail}
              >
                <div
                  className="SubscriptionActivate__AccountDetails__Modal__ResetEmail__Form__Content"
                >
                  <ResetEmail
                    input={{
                      isError: !!resetEmailErr,
                      name: USER_INFO_KEYS.RESET_EMAIL,
                      onChange: this.handleResetFormInputChange,
                      iconName: 'email-grey',
                      placeholder: intl.formatMessage({ id: 'views.subscriptionActivate.modal.resetEmail.formContent.email.placeholder' }),
                      value: userInfo[USER_INFO_KEYS.RESET_EMAIL],
                      onEnter: this.handleResetEmailPasswordSave,
                      errorText: resetEmailErr && intl.formatMessage({ id: resetEmailErr }),
                      autoFocus: true
                    }}
                    action={{
                      cssAuthButtonRootStyle: defaultStyle.AccountDetailsButtonStyle,
                      cssAuthButtonCssLabelStyle: defaultStyle.AccountDetailsButtonLabelStyle,
                      isProcessingAuth: accountDetails.isProcessingAuth,
                      onClick: this.handleResetEmailPasswordSave
                    }}
                  />
                </div>
              </Modal>
              {
                <AdditionalInfoModal
                  additionalInfoModalOpen={additionalInfoModalOpen}
                  errorInfoAuthForm={errorInfoAuthForm}
                  additionalInfo={additionalInfo}
                  closeAdditionalInfoModal={this.closeAdditionalInfoModal}
                  handleDobChange={this.handleDobChange}
                  handleAuthFormSave={this.handleAuthFormSave}
                  handleAuthFormInputChange={this.handleAuthFormInputChange}
                  userInfo={userInfo}
                  auth={auth}
                />
              }
              <div className="SubscriptionActivate__AccountDetails__Content__Details">
                {
                  isSignup ?
                    <FormattedHTMLMessage id="views.subscriptionActivate.accountDetails.contentDetails.signUp.description" />
                    : <FormattedHTMLMessage id="views.subscriptionActivate.accountDetails.contentDetails.signIn.description" />
                }
              </div>
              <div className="SubscriptionActivate__AccountDetails__FormCard">
                {
                  <React.Fragment>
                    <React.Fragment>
                      <div className="SubscriptionActivate__AccountDetails__FormCard__TabWrap">
                        <div
                          role="button"
                          tabIndex="0"
                          onClick={this.toggleView}
                          className={`SubscriptionActivate__AccountDetails__FormCard__TabWrap__Tab--Left ${(!isSignup && 'selected') || ''}`}
                        >
                          {
                            !isSignup &&
                            <img
                              className="Tabicon"
                              height="12"
                              width="12"
                              src="/images/icon/checkmark_white.png"
                              alt="icon"
                            />
                          }
                          <span>
                            <FormattedMessage id="views.subscriptionActivate.accountDetails.formCard.tabWrap.tab.signInLabel" />
                          </span>
                        </div>
                        <div
                          role="button"
                          tabIndex="0"
                          onClick={this.toggleView}
                          className={`SubscriptionActivate__AccountDetails__FormCard__TabWrap__Tab--Right ${(isSignup && 'selected') || ''}`}
                        >
                          {
                            !!isSignup &&
                            <img
                              className="Tabicon"
                              height="12"
                              width="12"
                              src="/images/icon/checkmark_white.png"
                              alt="icon"
                            />
                          }
                          <span>
                            <FormattedMessage id="views.subscriptionActivate.accountDetails.formCard.tabWrap.tab.signUpLabel" />
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                    <React.Fragment>
                      <div className="Auth__FormCard__Action">
                        <div className="Auth__FormCard__Action__SocialAuth">
                          <GoogleAuthButton
                            onClick={() => this.handleSocialAuthorisationContinue(AUTH0_CONNECTIONS.google)}
                            disabled={accountDetails.isProcessingAuth}
                            isSignup={isSignup}
                          />
                        </div>
                        <div className="Auth__FormCard__Action__SocialAuth">
                          <AppleAuthButton
                            onClick={() => this.handleSocialAuthorisationContinue(AUTH0_CONNECTIONS.apple)}
                            disabled={accountDetails.isProcessingAuth}
                            isSignup={isSignup}
                          />
                        </div>
                      </div>
                      <div className="SubscriptionActivate__AccountDetails__FormCard__Action__Separator">
                        <Divider />
                        <span className="SubscriptionActivate__AccountDetails__FormCard__Action__Separator__Text">
                          <FormattedMessage id="views.subscriptionActivate.accountDetails.formCard.action.separatorText" />
                        </span>
                      </div>
                    </React.Fragment>
                  </React.Fragment>
                }
                <React.Fragment>
                  {
                    isSignup &&
                    <div className="SubscriptionActivate__AccountDetails__NameSection">
                      <div className="SubscriptionActivate__AccountDetails__InputWrap">
                        <LabelledInput
                          name={USER_INFO_KEYS.FIRST_NAME}
                          onChange={this.handleAuthFormInputChange}
                          iconName="profile"
                          placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.accountDetails.inputWrap.firstName.placeholder' })}
                          value={userInfo[USER_INFO_KEYS.FIRST_NAME]}
                          onEnter={this.handleAuthFormSave}
                          errorText={errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME] &&
                            intl.formatMessage(
                              { id: errorInfoAuthForm[USER_INFO_KEYS.FIRST_NAME] }
                            )}
                        />
                      </div>
                      <div className="SubscriptionActivate__AccountDetails__InputWrap">
                        <LabelledInput
                          name={USER_INFO_KEYS.LAST_NAME}
                          onChange={this.handleAuthFormInputChange}
                          placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.accountDetails.inputWrap.lastName.placeholder' })}
                          value={userInfo[USER_INFO_KEYS.LAST_NAME]}
                          onEnter={this.handleAuthFormSave}
                          errorText={errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME] &&
                            intl.formatMessage(
                              { id: errorInfoAuthForm[USER_INFO_KEYS.LAST_NAME] }
                            )}
                        />
                      </div>
                    </div>
                  }
                </React.Fragment>
                {
                  <React.Fragment>
                    <div className={isSignup ? 'SubscriptionActivate__AccountDetails__InputWrap' : 'SubscriptionActivate__AccountDetails__InputWrap--Email--SignIn'}>
                      <LabelledInput
                        name={USER_INFO_KEYS.EMAIL}
                        onChange={this.handleAuthFormInputChange}
                        iconName="email-grey"
                        placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.accountDetails.inputWrap.email.placeholder' })}
                        id="AuthEmail"
                        value={userInfo[USER_INFO_KEYS.EMAIL]}
                        onEnter={this.handleAuthFormSave}
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.EMAIL] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.EMAIL] })}
                      />
                    </div>
                    <div className="SubscriptionActivate__AccountDetails__InputWrap LabelledInput__Input--Password">
                      <LabelledInput
                        name={USER_INFO_KEYS.PASSWORD}
                        onChange={this.handleAuthFormInputChange}
                        type="password"
                        placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.accountDetails.inputWrap.password.placeholder' })}
                        id="AuthPassword"
                        value={userInfo[USER_INFO_KEYS.PASSWORD]}
                        onEnter={this.handleAuthFormSave}
                        iconName="lock"
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.PASSWORD] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.PASSWORD] })}
                      />
                    </div>
                  </React.Fragment>
                }
                <React.Fragment>
                  {
                    isSignup &&
                    <div className="SubscriptionActivate__AccountDetails__InputWrap">
                      <LabelledInput
                        name={USER_INFO_KEYS.DOB}
                        onChange={this.handleDobChange}
                        iconName="dob"
                        placeholder={intl.formatMessage({ id: 'views.subscriptionActivate.accountDetails.inputWrap.dob.placeholder' })}
                        value={userInfo[USER_INFO_KEYS.DOB]}
                        onEnter={this.handleAuthFormSave}
                        errorText={errorInfoAuthForm[USER_INFO_KEYS.DOB] &&
                          intl.formatMessage({ id: errorInfoAuthForm[USER_INFO_KEYS.DOB] })}
                      />
                    </div>
                  }
                </React.Fragment>
                <div className="SubscriptionActivate__AccountDetails__FormCard__Action">
                  <Button
                    id="SubscriptionActivate__AccountDetails__Action"
                    cssStyles={{
                      root: defaultStyle.AccountDetailsButtonStyle,
                      label: defaultStyle.AccountDetailsButtonLabelStyle,
                    }}
                    className={`SubscriptionActivate__AccountDetails__ActionButton ${(accountDetails.isProcessingAuth && 'Disable') || ''}`}
                    disabled={accountDetails.isProcessingAuth}
                    onClick={this.handleAuthFormSave}
                  >
                    {
                    isSignup ?
                      <FormattedMessage id="views.subscriptionActivate.accountDetails.actionButton.signUp.buttonLabel" /> :
                      <FormattedMessage id="views.subscriptionActivate.accountDetails.actionButton.signIn.buttonLabel" />
                    }
                  </Button>
                </div>
                <div className="SubscriptionActivate__AccountDetails__FormCard__Note">
                  {
                    !isSignup &&
                    <div>
                      <span
                        role="button"
                        tabIndex="0"
                        className="SubscriptionActivate__AccountDetails__FormCard__Note__Link"
                        onClick={this.showResetPasswordModal}
                        style={{
                          outline: 'none'
                        }}
                      >
                        <FormattedMessage id="views.subscriptionActivate.accountDetails.resetPassword.actionLabel" />
                      </span>
                    </div>
                  }
                </div>
              </div>
            </div>
        }
      </div>
    );
  }
}

AccountDetails.propTypes = {
  isActive: PropTypes.bool.isRequired,
  currentSection: PropTypes.string.isRequired,
  setCurrentSection: PropTypes.func.isRequired,
  sectionHeader: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  loginAccountDetails: PropTypes.func.isRequired,
  signupAccountDetails: PropTypes.func.isRequired,
  continueWithSocialAccountDetails: PropTypes.func.isRequired,
  resetEmailAccountDetails: PropTypes.func.isRequired,
  loginAccountDetailsRecurring: PropTypes.func.isRequired,
  saveAddtionalInfoAccountDetails: PropTypes.func.isRequired,
  handleFetchUser: PropTypes.func.isRequired,
  app: PropTypes.string.isRequired,
  windowDimension: PropTypes.object.isRequired,
  subscriptionActivate: PropTypes.object.isRequired,
  handleLogoutUser: PropTypes.func.isRequired,
  activationCode: PropTypes.string.isRequired,
  checkoutSuccess: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired
};

function mapStateToProps({ auth, subscriptionActivate }) {
  return { auth, subscriptionActivate };
}

export default connect(
  mapStateToProps,
  {
    loginAccountDetails,
    signupAccountDetails,
    resetEmailAccountDetails,
    loginAccountDetailsRecurring,
    saveAddtionalInfoAccountDetails,
    continueWithSocialAccountDetails,
    handleFetchUser,
    handleLogoutUser
  }
)(injectIntl(AccountDetails));



// WEBPACK FOOTER //
// ./src/views/SubscriptionActivate/AccountDetails/AccountDetails.jsx